import React, { useEffect, useState } from "react";

import { RequireUserAuth, useUserContext } from "../context/User";
import { getUserProfile, updateUserProfile } from "../services/api";
import { PetFileType } from "../types/pet";
import { UserProfileAPIResponseType } from "../types/user";
import Button from "./Button";
import ImageUpload from "./ImageUpload";
import SideBarContent from "./SideBarContent";
import SideBarFooter from "./SideBarFooter";
import Input from "./ui/Input";
import Select from "./ui/Select";
import Textarea from "./ui/Textarea";

const defaultProfile = {
  title: "",
  desc: "",
  country: "",
  street: "",
  city: "",
  postcode: "",
  phone: "",
  website: "",
  logo: {},
  header: {},
};
const staticUrl: string = process.env.STATIC_URL;

type PropsType = {
  onCloseHandle: () => void;
};

type Errors = {
  [s: string]: boolean;
};

function UserPageOverview({ onCloseHandle }: PropsType) {
  const [logo, setLogo] = useState({} as PetFileType);
  const [header, setHeader] = useState({} as PetFileType);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(defaultProfile as UserProfileAPIResponseType);
  const [errors, setErrors] = useState({} as Errors);

  const { user } = useUserContext();

  useEffect(() => {
    if (!user.accessToken) {
      return;
    }

    fetchUserProfile();
  }, [user.accessToken]);

  useEffect(() => {
    // console.log("header", header);
  }, [header]);

  useEffect(() => {
    // console.log("logo", logo);
  }, [logo]);

  useEffect(() => {
    // console.log("ERRORS", errors);
  }, [errors]);

  const fetchUserProfile = () => {
    if (!user.accessToken) {
      return;
    }

    getUserProfile(user.accessToken)
      .then((data) => {
        if (!data) {
          return;
        }
        setProfile(data);
        if (data.logo) {
          setLogo({
            preview: `${staticUrl}${data.logo.url}`,
            id: data.logo.id,
          });
        }
        if (data.header) {
          setHeader({
            preview: `${staticUrl}${data.header.url}`,
            id: data.header.id,
          });
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false));
  };

  const onSaveProfile = () => {
    if (!user.accessToken) {
      return;
    }

    const status = validateFields();
    if (!status) {
      return;
    }

    setLoading(true);

    const profileToSave = { ...profile };
    delete profileToSave.logo;
    delete profileToSave.header;

    const fd = new FormData();
    Object.entries(profileToSave).map((entry) => {
      const [key, value] = entry;
      fd.append(key, value);
    });
    if (logo.data) {
      fd.append("logo", logo.data);
    }
    if (header.data) {
      fd.append("header", header.data);
    }

    updateUserProfile(user.accessToken, fd)
      .then(() => fetchUserProfile())
      .catch((e) => console.log(e))
      .finally(() => setLoading(false));
  };

  const renderFooter = () => {
    return (
      <div className="flex flex-row items-center justify-end gap-4">
        <div onClick={onCloseHandle} className="cursor-pointer font-simianregular text-[18px]">
          Cancel
        </div>
        <Button title="Save changes" onClick={onSaveProfile} theme="dark" />
      </div>
    );
  };

  const validateFields = () => {
    const er = {};
    Object.entries(defaultProfile).map((el) => {
      const [key] = el;
      if (typeof profile[key] === "string" && profile[key].length <= 1) {
        er[key] = true;
      }
      if (key === "logo" && Object.keys(logo).length === 0) {
        er[key] = true;
      }
      if (key === "header" && Object.keys(header).length === 0) {
        er[key] = true;
      }
    });

    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const handleLogoChange = (fileList: FileList) => {
    // const file = e.target.files[0];
    console.log("file", fileList[0]);
    const img = {
      preview: URL.createObjectURL(fileList[0]),
      data: fileList[0],
    };
    setLogo(img);
  };

  const handleHeaderChange = (fileList: FileList) => {
    // const file = e.target.files[0];
    const img = {
      preview: URL.createObjectURL(fileList[0]),
      data: fileList[0],
    };
    setHeader(img);
  };

  if (loading) {
    return;
  }

  return (
    <>
      <SideBarContent>
        <div className="mb-10 flex flex-col gap-10 text-[#474747]">
          <div className="flex flex-col gap-4">
            <div className="font-simianregular text-[20px] leading-none text-[#0B0B0B]">
              Shelter logo
            </div>
            <div>
              Ensure your uploaded image are high-quality JPEG or PNG files, with a maximum size of
              2 MB per file.
            </div>
            <ImageUpload files={[logo]} onChangeHandler={handleLogoChange} err={errors.logo} />
          </div>

          <div className="flex flex-col gap-4">
            <div className="font-simianregular text-[20px] leading-none text-[#0B0B0B]">
              Cover image
            </div>
            <div>
              Please upload a cover image that highlights the best aspects of your dog shelter - the
              happy environment, staff-dog bonds, or unique features. A compelling image not only
              reflects your shelter&apos;s essence but also attracts potential adopters and
              donators.
            </div>
            <div>
              Ensure your uploaded image are high-quality JPEG or PNG files, with a maximum size of
              2 MB per file.
            </div>
            <ImageUpload
              files={[header]}
              onChangeHandler={handleHeaderChange}
              err={errors.header}
            />
          </div>

          <div className="flex max-w-[850px] flex-col gap-4">
            <div className="font-simianregular text-[20px] leading-none text-[#0B0B0B]">
              Shelter description
            </div>
            <div>
              <Input
                name="title"
                label="Shelter title"
                value={profile.title}
                onChange={(e) =>
                  setProfile((prevState) => {
                    return { ...prevState, title: e.target.value };
                  })
                }
                error={errors.title}
              />
            </div>
            <div>
              Craft a compelling shelter description to connect with potential adopters. Eg. share
              your mission, highlight your facilities, introduce your dedicated team, showcase
              success stories. Your words help dogs find loving homes.
            </div>
            <div>
              <Textarea
                name="desc"
                label="About shelter"
                value={profile.desc}
                rows={10}
                onChange={(e) =>
                  setProfile((prevState) => {
                    return { ...prevState, desc: e.target.value };
                  })
                }
                error={errors.desc}
              />
            </div>
          </div>

          <div className="flex max-w-[850px] flex-col gap-4">
            <div className="font-simianregular text-[20px] leading-none text-[#0B0B0B]">
              Contact
            </div>
            <div>
              <Input
                name="street"
                label="Street"
                value={profile.street}
                onChange={(e) =>
                  setProfile((prevState) => {
                    return { ...prevState, street: e.target.value };
                  })
                }
                error={errors.street}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Input
                  name="city"
                  label="City"
                  value={profile.city}
                  onChange={(e) =>
                    setProfile((prevState) => {
                      return { ...prevState, city: e.target.value };
                    })
                  }
                  error={errors.city}
                />
              </div>
              <div>
                <Input
                  name="postcode"
                  label="Postal code"
                  value={profile.postcode}
                  onChange={(e) =>
                    setProfile((prevState) => {
                      return { ...prevState, postcode: e.target.value };
                    })
                  }
                  error={errors.postcode}
                />
              </div>
            </div>
            <div>
              <Input
                name="country"
                label="Country"
                value={profile.country}
                onChange={(e) =>
                  setProfile((prevState) => {
                    return { ...prevState, country: e.target.value };
                  })
                }
                error={errors.country}
              />
            </div>
            <div>
              <Input
                name="website"
                label="Website"
                value={profile.website}
                onChange={(e) =>
                  setProfile((prevState) => {
                    return { ...prevState, website: e.target.value };
                  })
                }
                error={errors.website}
              />
            </div>
            <div>
              <Input
                name="phone"
                label="Phone"
                value={profile.phone}
                onChange={(e) =>
                  setProfile((prevState) => {
                    return { ...prevState, phone: e.target.value };
                  })
                }
                error={errors.phone}
              />
            </div>
          </div>
        </div>
      </SideBarContent>
      <SideBarFooter>{renderFooter()}</SideBarFooter>
    </>
  );
}

export default function UserPageOverviewWrapper(props: PropsType) {
  return (
    <RequireUserAuth>
      <UserPageOverview {...props} />
    </RequireUserAuth>
  );
}
